// client/src/components/favorites/FavoriteButton.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useFavorites } from '../../contexts/FavoritesContext';

const FavoriteButton = ({ productId, size = 'md', className = '' }) => {
  const { t } = useTranslation('business');
  const { isAuthenticated, user } = useAuth();
  const { isFavorited, addFavorite, removeFavorite } = useFavorites();
  const [isLoading, setIsLoading] = useState(false);

  // ✅ INSTANT check - no API call needed!
  const favorited = isFavorited(productId);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast(t('productDetails.loginRequired'));
      return;
    }

    if (user?.role !== 'business') {
      toast(t('catalog.businessUserRequired'));
      return;
    }

    setIsLoading(true);

    try {
      if (favorited) {
        const result = await removeFavorite(productId);
        if (result.success) {
          toast.success('Removed from favorites');
        } else {
          toast.error(result.message);
        }
      } else {
        const result = await addFavorite(productId);
        if (result.success) {
          toast.success('Added to favorites');
        } else {
          toast.error(result.message);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated || user?.role !== 'business') {
    return null;
  }

  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  };

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={isLoading}
      className={`
        ${sizes[size]} 
        rounded-full 
        flex items-center justify-center
        transition-all duration-200
        ${favorited 
          ? 'bg-red-50 hover:bg-red-100' 
          : 'bg-white hover:bg-gray-100'
        }
        border-2
        ${favorited ? 'border-red-500' : 'border-gray-300'}
        shadow-sm hover:shadow-md
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      title={favorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Heart
        size={iconSizes[size]}
        className={`
          transition-all duration-200
          ${favorited 
            ? 'fill-red-500 text-red-500' 
            : 'text-gray-400'
          }
          ${isLoading ? 'animate-pulse' : ''}
        `}
      />
    </button>
  );
};

export default FavoriteButton;